import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

const IMG = {
    topdown: "/assets/synara/pc-topdown.jpeg",
    chefDuas: "/assets/synara/pc-chef-duas.jpeg",
    chefUma: "/assets/synara/pc-chef-uma.jpeg",
    abertas: "/assets/synara/pc-abertas.jpeg",
    gratinada: "/assets/synara/pc-gratinada.jpeg",
};

const ease = [0.16, 1, 0.3, 1];

const ImageCard = ({ src, alt, className = "", delay = 0, testid }) => (
    <motion.figure
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay, ease }}
        data-testid={testid}
        className={`group relative overflow-hidden rounded-2xl ring-1 ring-stone-200 bg-stone-100 ${className}`}
    >
        <img
            src={src}
            alt={alt}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-stone-900/25 via-transparent to-transparent opacity-60" />
    </motion.figure>
);

export const MealGallery = () => {
    const { t } = useTranslation();
    return (
        <section
            id="galeria"
            data-testid="meal-gallery-section"
            className="py-24 md:py-32 bg-stone-50"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {/* Header */}
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14 md:mb-20">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="gold-divider" />
                            <span className="text-xs uppercase tracking-[0.24em] text-stone-500">
                                {t("gallery.eyebrow")}
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl text-stone-900 tracking-tight leading-[1.05]">
                            {t("gallery.title_1")}{" "}
                            <em className="italic text-gold-deep font-normal">
                                {t("gallery.title_em")}
                            </em>
                            .
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                        <p className="text-lg text-stone-600 font-light leading-relaxed">
                            {t("gallery.subtitle")}
                        </p>
                    </div>
                </div>

                {/* Bento grid */}
                <div className="grid grid-cols-12 gap-4 md:gap-5 auto-rows-[160px] sm:auto-rows-[200px] md:auto-rows-[220px]">
                    {/* Hero top-down — large */}
                    <ImageCard
                        src={IMG.topdown}
                        alt="Marmitas alinhadas com frango desfiado, carne, arroz e legumes salteados"
                        className="col-span-12 md:col-span-8 row-span-2"
                        delay={0}
                        testid="gallery-img-topdown"
                    />
                    {/* Chef com duas marmitas */}
                    <ImageCard
                        src={IMG.chefDuas}
                        alt="Synara a sorrir, segurando duas marmitas de vidro com arroz e legumes"
                        className="col-span-6 md:col-span-4 row-span-1"
                        delay={0.1}
                        testid="gallery-img-chef-duas"
                    />
                    {/* Chef com uma marmita */}
                    <ImageCard
                        src={IMG.chefUma}
                        alt="Synara a apresentar marmita com bife, legumes e arroz"
                        className="col-span-6 md:col-span-4 row-span-1"
                        delay={0.15}
                        testid="gallery-img-chef-uma"
                    />
                    {/* Marmitas abertas */}
                    <ImageCard
                        src={IMG.abertas}
                        alt="Marmitas de vidro empilhadas e abertas com arroz, legumes salteados e proteínas"
                        className="col-span-7 md:col-span-7 row-span-1"
                        delay={0.2}
                        testid="gallery-img-abertas"
                    />
                    {/* Gratinado close-up */}
                    <ImageCard
                        src={IMG.gratinada}
                        alt="Massa com molho bolonhesa gratinada com queijo em marmitas de vidro"
                        className="col-span-5 md:col-span-5 row-span-1"
                        delay={0.25}
                        testid="gallery-img-gratinada"
                    />
                </div>

                {/* Caption / micro CTA */}
                <motion.p
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="mt-10 md:mt-14 text-center text-sm uppercase tracking-[0.24em] text-stone-500"
                >
                    {t("gallery.caption")}
                </motion.p>
            </div>
        </section>
    );
};

export default MealGallery;
